import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/api';
import { toast } from 'sonner';
import { 
  Clock, CheckCircle, AlertCircle, RefreshCw, ExternalLink, 
  ChevronRight, Bell, Loader, Mail, Globe, Play, Check
} from 'lucide-react';

// Son de notification
const NOTIFICATION_SOUND = 'data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdH2Onq2op5yUjYWDg4eLkZmdoqGdmJGLh4aHi5CXnaGjoZ2YkYuHhoeLkZecoaOinZiRi4eGh4uRl5yhoqKdmJGLh4aHi5GXnKGiop2YkYuHhoeLkZeco6OinZiRi4eGh4uRl5yjoqKdmJGLh4aHi5GXnKOiop2YkYuHhg==';

const STATUS_CONFIG = {
  waiting: { 
    label: 'En attente', 
    color: 'text-amber-600', 
    bg: 'bg-amber-50', 
    border: 'border-amber-200',
    icon: Clock 
  },
  ready: { 
    label: 'Prêt', 
    color: 'text-emerald-600', 
    bg: 'bg-emerald-50', 
    border: 'border-emerald-200',
    icon: Bell 
  },
  in_progress: { 
    label: 'En cours', 
    color: 'text-blue-600', 
    bg: 'bg-blue-50', 
    border: 'border-blue-200',
    icon: Play 
  },
  completed: { 
    label: 'Terminé', 
    color: 'text-gray-500', 
    bg: 'bg-gray-50', 
    border: 'border-gray-200',
    icon: CheckCircle 
  }
};

export default function PendingTasks({ showCompleted = false, compact = false }) {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedTask, setExpandedTask] = useState(null);
  const [verifying, setVerifying] = useState(null);
  const previousReadyCount = useRef(0);
  const audioRef = useRef(null);

  // Charger les tâches
  const loadTasks = useCallback(async () => {
    try {
      const res = await api.get(`/tasks?include_completed=${showCompleted}`);
      const newTasks = res.data;
      
      // Compter les tâches "ready"
      const readyCount = newTasks.filter(t => t.status === 'ready').length;
      
      // Notification si nouvelles tâches prêtes
      if (readyCount > previousReadyCount.current && previousReadyCount.current !== 0) {
        const newReadyTasks = newTasks.filter(t => t.status === 'ready');
        
        // Son
        if (audioRef.current) {
          audioRef.current.play().catch(() => {});
        }
        
        // Toast
        newReadyTasks.forEach(task => {
          toast.success(`Configuration prête pour ${task.domain}`, {
            description: 'Cliquez pour configurer',
            duration: 10000,
            action: {
              label: 'Voir',
              onClick: () => setExpandedTask(task.id)
            }
          });
        });
      }
      
      previousReadyCount.current = readyCount;
      setTasks(newTasks);
    } catch (e) {
      console.error('Error loading tasks:', e);
    } finally {
      setLoading(false);
    }
  }, [showCompleted]);

  // Charger au montage et toutes les 15 secondes
  useEffect(() => {
    loadTasks();
    const interval = setInterval(loadTasks, 15000); // 15 secondes pour test, 15 min en prod
    return () => clearInterval(interval);
  }, [loadTasks]);

  // Vérifier une étape
  const verifyStep = async (taskId, stepName) => {
    setVerifying(`${taskId}-${stepName}`);
    try {
      const res = await api.post(`/tasks/${taskId}/verify?step_name=${stepName}`);
      
      if (res.data.success) {
        toast.success(res.data.message);
        loadTasks();
      } else {
        toast.info(res.data.message);
      }
    } catch (e) {
      toast.error('Erreur lors de la vérification');
    } finally {
      setVerifying(null);
    }
  };

  // Marquer une étape comme complétée
  const markStepComplete = async (taskId, stepName) => {
    try {
      await api.put(`/tasks/${taskId}/step/${stepName}/complete`);
      toast.success('Étape marquée comme complétée');
      loadTasks();
    } catch (e) {
      toast.error('Erreur');
    }
  };

  // Calculer le temps restant
  const getTimeRemaining = (readyAt) => {
    const now = new Date();
    const ready = new Date(readyAt);
    const diff = ready - now;
    
    if (diff <= 0) return null;
    
    const minutes = Math.floor(diff / 60000);
    const seconds = Math.floor((diff % 60000) / 1000);
    
    if (minutes > 0) return `${minutes} min ${seconds}s`;
    return `${seconds}s`;
  };

  // Filtrer les tâches non-annulées
  const filteredTasks = tasks.filter(t => t.status !== 'cancelled');
  const pendingTasks = filteredTasks.filter(t => t.status !== 'completed');
  const completedTasks = filteredTasks.filter(t => t.status === 'completed');

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center justify-center gap-2 text-gray-500">
          <Loader size={18} className="animate-spin" />
          <span>Chargement des tâches...</span>
        </div>
      </div>
    );
  }

  if (filteredTasks.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6 text-center text-gray-500">
        <CheckCircle size={32} className="mx-auto mb-2 text-gray-300" />
        <p>Aucune tâche en attente</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Audio pour notification */}
      <audio ref={audioRef} src={NOTIFICATION_SOUND} preload="auto" />
      
      {/* Tâches en attente */}
      {pendingTasks.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Clock size={18} className="text-amber-500" />
              <h3 className="font-semibold text-gray-900">Tâches en attente</h3>
              <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded-full">
                {pendingTasks.length}
              </span>
            </div>
            <button
              onClick={loadTasks}
              className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
              title="Actualiser"
            >
              <RefreshCw size={16} className="text-gray-500" />
            </button>
          </div>
          
          <div className="divide-y divide-gray-50">
            {pendingTasks.map(task => {
              const config = STATUS_CONFIG[task.status];
              const Icon = config.icon;
              const timeRemaining = task.status === 'waiting' ? getTimeRemaining(task.ready_at) : null;
              const isExpanded = expandedTask === task.id;
              
              return (
                <div key={task.id} className={`${config.bg} ${config.border} border-l-4`}>
                  {/* Header */}
                  <div 
                    className="px-4 py-3 flex items-center justify-between cursor-pointer hover:bg-white/50 transition-colors"
                    onClick={() => setExpandedTask(isExpanded ? null : task.id)}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${config.bg}`}>
                        <Icon size={16} className={config.color} />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{task.client_name}</p>
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Globe size={12} />
                          <span>{task.domain}</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      {timeRemaining ? (
                        <span className="text-sm text-amber-600 font-medium">
                          Prêt dans {timeRemaining}
                        </span>
                      ) : task.status === 'ready' ? (
                        <span className="px-2 py-1 bg-emerald-500 text-white text-xs font-bold rounded-full animate-pulse">
                          PRÊT !
                        </span>
                      ) : null}
                      <ChevronRight 
                        size={18} 
                        className={`text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} 
                      />
                    </div>
                  </div>
                  
                  {/* Expanded content */}
                  {isExpanded && (
                    <div className="px-4 pb-4 space-y-3">
                      <div className="bg-white rounded-lg border border-gray-200 divide-y divide-gray-100">
                        {task.steps.map((step, idx) => {
                          const stepReady = !getTimeRemaining(step.ready_at);
                          const isVerifying = verifying === `${task.id}-${step.name}`;
                          
                          return (
                            <div key={step.name} className="p-3">
                              <div className="flex items-start justify-between mb-2">
                                <div className="flex items-center gap-2">
                                  <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                                    step.status === 'completed' 
                                      ? 'bg-emerald-500 text-white' 
                                      : 'bg-gray-200 text-gray-600'
                                  }`}>
                                    {step.status === 'completed' ? <Check size={12} /> : idx + 1}
                                  </span>
                                  <span className={`font-medium ${step.status === 'completed' ? 'text-gray-400 line-through' : 'text-gray-900'}`}>
                                    {step.label}
                                  </span>
                                </div>
                                
                                {step.status !== 'completed' && stepReady && (
                                  <div className="flex gap-2">
                                    <button
                                      onClick={() => verifyStep(task.id, step.name)}
                                      disabled={isVerifying}
                                      className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50"
                                    >
                                      {isVerifying ? (
                                        <Loader size={12} className="animate-spin" />
                                      ) : (
                                        <RefreshCw size={12} />
                                      )}
                                      Vérifier
                                    </button>
                                    <button
                                      onClick={() => markStepComplete(task.id, step.name)}
                                      className="flex items-center gap-1 px-3 py-1.5 bg-emerald-600 text-white text-xs font-medium rounded-lg hover:bg-emerald-700"
                                    >
                                      <Check size={12} />
                                      Fait
                                    </button>
                                  </div>
                                )}
                              </div>
                              
                              {/* Instructions */}
                              {step.status !== 'completed' && step.instructions && (
                                <div className="ml-8 mt-2">
                                  <ol className="text-sm text-gray-600 space-y-1 list-decimal list-inside">
                                    {step.instructions.map((instr, i) => (
                                      <li key={`instr-${i}`}>{instr}</li>
                                    ))}
                                  </ol>
                                  <a
                                    href="https://www.ovh.com/manager/#/web/zimbra"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1 mt-2 text-sm text-blue-600 hover:text-blue-800"
                                  >
                                    <ExternalLink size={12} />
                                    Ouvrir OVH Zimbra
                                  </a>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                      
                      {/* Lien vers le client */}
                      <Link
                        to={`/clients/${task.client_id}`}
                        className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900"
                      >
                        Voir la fiche client
                        <ChevronRight size={14} />
                      </Link>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
      
      {/* Tâches terminées */}
      {showCompleted && completedTasks.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2">
            <CheckCircle size={18} className="text-gray-400" />
            <h3 className="font-semibold text-gray-500">Historique</h3>
            <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs font-medium rounded-full">
              {completedTasks.length}
            </span>
          </div>
          
          <div className="divide-y divide-gray-50">
            {completedTasks.slice(0, 5).map(task => (
              <div key={task.id} className="px-4 py-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <CheckCircle size={16} className="text-emerald-500" />
                  <div>
                    <p className="text-sm font-medium text-gray-700">{task.client_name}</p>
                    <p className="text-xs text-gray-500">{task.domain}</p>
                  </div>
                </div>
                <span className="text-xs text-gray-400">
                  {new Date(task.completed_at).toLocaleDateString('fr-FR')}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
